/**
 * screens-orders.ts — ออเดอร์และเงินโอน (T096)
 *
 * กติกาที่หน้าจอต้องสื่อให้ชัด:
 * - “วันที่รับรู้” = วันที่แพลตฟอร์มโอนเงินจริง (paid_time) — แสดงแยกจาก statement_time
 * - ออเดอร์ที่ยังไม่โอน (paid_time ว่าง) ยังไม่ถูกรับรู้ในรายงาน → เตือนไว้
 * - รายการคืนรับรู้วันที่คืน และ link กลับออเดอร์เดิม
 */
import type { Page } from './router.js';
import { getTenant } from './state.js';
import * as api from './api.js';
import {
  esc,
  money,
  pageShell,
  dataTable,
  pill,
  opt,
  errorBox,
  pagerHtml,
  bindPager,
  showDrawer,
  drawerHtml,
} from './ui.js';

type Row = Record<string, unknown>;

const CHANNEL_LABELS: Record<string, string> = {
  tiktok_shop: 'TikTok Shop',
  shopee: 'Shopee',
  lazada: 'Lazada',
  facebook: 'Facebook',
  line: 'LINE',
};

const ts = (v: unknown): string =>
  v ? String(v).replace('T', ' ').slice(0, 16) : '—';

/**
 * แผงเชื่อม TikTok Shop + ซิงค์ออเดอร์ (T054/T056)
 * ใช้ได้เมื่อตั้ง credential แล้ว; ถ้ายังไม่ตั้ง endpoint คืน 404 → แสดงข้อความแทน
 */
export async function mountTikTok(host: HTMLElement, onSynced?: () => void): Promise<void> {
  // แจ้งผลจาก callback ที่ main.ts เก็บไว้ (connected:<n> หรือ error:<reason>)
  let banner = '';
  try {
    const r = sessionStorage.getItem('tt_result');
    if (r) {
      sessionStorage.removeItem('tt_result');
      const [kind, detail] = r.split(/:(.*)/s);
      banner =
        kind === 'connected'
          ? `<p class="ok-note">เชื่อม TikTok Shop สำเร็จ (${esc(detail ?? '0')} ร้าน)</p>`
          : `<p class="warn-note">เชื่อม TikTok ไม่สำเร็จ: ${esc(detail ?? '')}</p>`;
    }
  } catch {
    /* sessionStorage อาจถูกบล็อก — ข้าม banner */
  }

  const render = (inner: string): void => {
    host.innerHTML = banner + inner;
  };

  let shops: api.TikTokShop[];
  try {
    shops = (await api.tiktokListShops()).items;
  } catch (e) {
    // 404 = ยังไม่ตั้ง credential; อื่น ๆ = error จริง
    render(
      `<p class="muted">ยังไม่ได้เปิดการเชื่อม TikTok บนระบบนี้ (ต้องตั้ง app credential ฝั่งเซิร์ฟเวอร์)</p>
       <p class="foot-note">${esc((e as Error).message)}</p>`,
    );
    return;
  }

  const list = shops.length
    ? `<table class="data"><thead><tr><th>ร้าน</th><th>Shop ID</th><th>ภูมิภาค</th><th></th></tr></thead><tbody>${shops
        .map(
          (s) =>
            `<tr><td>${esc(s.shop_name ?? '—')}</td><td class="muted">${esc(s.shop_id)}</td><td>${esc(
              s.region ?? '—',
            )}</td><td><button class="mini" type="button" data-sync="${esc(s.shop_id)}">🔄 ซิงค์ข้อมูล</button></td></tr>`,
        )
        .join('')}</tbody></table>
       <p class="foot-note">ซิงค์ดึง <strong>สินค้า · ออเดอร์ · รอบเงินโอน · การคืน</strong> · ระบบซิงค์อัตโนมัติทุกชั่วโมง กดปุ่มเพื่อดึงทันที</p>`
    : `<p class="muted">ยังไม่มีร้าน TikTok ที่เชื่อม</p>
       <p class="foot-note">กด "เชื่อมร้าน TikTok Shop" เพื่ออนุญาตให้ระบบดึงข้อมูลออเดอร์/เงินโอนอัตโนมัติ</p>`;

  render(
    `${list}
     <div class="form-actions" style="margin-top:10px">
       <button type="button" id="tiktok-connect">+ เชื่อมร้าน TikTok Shop</button>
       <span id="tiktok-msg" class="muted"></span>
     </div>`,
  );

  const msg = host.querySelector<HTMLElement>('#tiktok-msg')!;
  host.querySelector<HTMLButtonElement>('#tiktok-connect')?.addEventListener('click', async () => {
    msg.textContent = 'กำลังเปิดหน้าอนุญาต…';
    try {
      const { authorizeUrl } = await api.tiktokConnect();
      try {
        sessionStorage.setItem('tt_pending', '1'); // ให้ main.ts รู้ว่ากำลังรอ callback
      } catch {
        /* ignore */
      }
      window.location.href = authorizeUrl; // ไปหน้า authorize ของ TikTok
    } catch (e) {
      msg.textContent = (e as Error).message;
    }
  });
  host.querySelectorAll<HTMLButtonElement>('[data-sync]').forEach((btn) => {
    btn.addEventListener('click', async () => {
      btn.disabled = true;
      msg.className = 'muted';
      msg.textContent = '⏳ กำลังซิงค์ข้อมูล…';
      try {
        const r = await api.tiktokSync(btn.dataset['sync']!);
        const prod = r.products ? ` · ${r.products.itemCount} SKU` : '';
        const stmt = r.settlements ? ` · ${r.settlements.itemCount} รอบโอนเงิน` : '';
        const rf = r.refunds ? ` · ${r.refunds.itemCount} คืนเงิน` : '';
        if (r.skipped) {
          msg.textContent = 'ร้านนี้กำลังซิงค์อยู่ ลองใหม่ภายหลัง';
        } else {
          msg.className = 'ok-text';
          msg.textContent = `✓ ซิงค์สำเร็จ: ${r.itemCount} ออเดอร์${prod}${stmt}${rf}`;
          onSynced?.(); // รีเฟรชตารางออเดอร์/เงินโอน/คืนเงินอัตโนมัติ
        }
      } catch (e) {
        msg.className = 'warn-text';
        msg.textContent = (e as Error).message;
      } finally {
        btn.disabled = false;
      }
    });
  });
}

export const ordersPage: Page = {
  title: 'ออเดอร์และเงินโอน',
  render: () =>
    pageShell(
      'ออเดอร์และเงินโอน',
      `<section class="card" id="tiktok-card">
        <h3>TikTok Shop</h3>
        <div id="tiktok-body" class="muted">กำลังโหลด…</div>
      </section>
      <section class="card">
        <form id="order-filter" class="grid-form" role="search">
          <label>วันที่รับรู้ (เงินเข้า) ตั้งแต่<input name="from" type="date"></label>
          <label>ถึง<input name="to" type="date"></label>
          <label>ช่องทาง<select name="channelCode"><option value="">ทั้งหมด</option>${Object.entries(
            CHANNEL_LABELS,
          )
            .map(([v, l]) => `<option value="${v}">${esc(l)}</option>`)
            .join('')}</select></label>
          <label>ค้นหาเลขออเดอร์<input name="search" type="search" placeholder="เช่น TT-1001"></label>
          <label class="check"><input name="unpaidOnly" type="checkbox"> เฉพาะที่แพลตฟอร์มยังไม่โอน</label>
          <div class="form-actions"><button type="submit">กรอง</button><span id="order-msg" class="muted"></span></div>
        </form>
        <p class="foot-note">วันที่รับรู้ใช้เวลาที่แพลตฟอร์มโอนเงินจริง — <code>statement_time</code> เก็บแยกและไม่ถูกใช้แทน</p>
        <div id="order-list">กำลังโหลด…</div>
      </section>
      <section class="card"><h3>รอบเงินโอน (settlement)</h3><div id="settlement-list">กำลังโหลด…</div></section>
      <section class="card"><h3>การคืนสินค้า/คืนเงิน</h3>
        <p class="foot-note">รับรู้ในวันที่คืน — ไม่แก้ยอดขายของงวดเดิม</p>
        <div id="refund-list">กำลังโหลด…</div></section>
      ${drawerHtml}`,
    ),
  mount(root) {
    if (!getTenant()) return;
    const el = (id: string): HTMLElement => root.querySelector<HTMLElement>(`#${id}`)!;
    const filterForm = root.querySelector<HTMLFormElement>('#order-filter')!;
    const state = { limit: 25, offset: 0 };

    void mountTikTok(el('tiktok-body'), () => {
      // หลังซิงค์สำเร็จ รีเฟรชตารางทั้งหมดให้เห็นข้อมูลใหม่ทันที (ไม่ต้องรีโหลดหน้า)
      state.offset = 0;
      void refresh();
      void loadSettlements();
      void loadRefunds();
    });

    const openOrder = async (id: string): Promise<void> => {
      showDrawer(root, 'รายละเอียดออเดอร์', '<p class="muted">กำลังโหลด…</p>');
      const o = await api.getOrder(id);
      const lines = (o['order_lines'] as Row[] | undefined) ?? [];
      const refunds = (o['returns_refunds'] as Row[] | undefined) ?? [];
      const txs = (o['settlement_transactions'] as Row[] | undefined) ?? [];
      showDrawer(
        root,
        `ออเดอร์ ${String(o['external_order_id'])}`,
        `<p class="muted">ช่องทาง ${esc(CHANNEL_LABELS[String(o['channel_code'])] ?? o['channel_code'])} ·
          สั่งซื้อ ${ts(o['order_time'])} · ชำระ ${ts(o['payment_time'])} · สำเร็จ ${ts(o['completed_time'])}</p>
        <p class="muted"><strong>วันที่รับรู้ (เงินเข้า): ${ts(o['paid_time'])}</strong> · statement_time: ${ts(o['statement_time'])}</p>
        <h4>รายการสินค้า</h4>` +
          dataTable(
            ['SKU (จากช่องทาง)', 'ชื่อ', 'จำนวน', 'ราคา/หน่วย', 'ต้นทุน snapshot', 'สถานะข้อมูล'],
            lines.map((l) => ({
              cls: l['completeness'] === 'complete' ? undefined : 'row-warn',
              cells: [
                esc(l['external_sku_id'] ?? '—'),
                esc(l['external_name'] ?? '—'),
                esc(l['quantity']),
                money(l['unit_price']),
                l['cost_unit_snapshot'] != null
                  ? money(l['cost_unit_snapshot'])
                  : '<span class="error">ยังไม่มีต้นทุน</span>',
                esc(l['completeness']),
              ],
            })),
            'ไม่มีรายการสินค้า',
          ) +
          '<h4>ค่าธรรมเนียมและเงินโอน</h4>' +
          dataTable(
            ['เวลา', 'ประเภท', 'คอมมิชชัน', 'Affiliate', 'ค่าส่ง', 'ชดเชย', 'ปรับปรุง', 'สุทธิ'],
            txs.map((t) => ({
              cells: [
                ts(t['transaction_time']),
                esc(t['type'] ?? '—'),
                money(t['commission_fee']),
                money(t['affiliate_fee']),
                money(t['shipping_fee']),
                money(t['compensation']),
                money(t['adjustment']),
                money(t['net_amount']),
              ],
            })),
            'ยังไม่มีรายการค่าธรรมเนียม/เงินโอนของออเดอร์นี้',
          ) +
          '<h4>การคืน</h4>' +
          dataTable(
            ['วันที่คืน', 'จำนวน', 'ยอดคืน', 'เหตุผล'],
            refunds.map((r) => ({
              cells: [ts(r['refund_time']), esc(r['quantity']), money(r['refund_amount']), esc(r['reason'] ?? '—')],
            })),
            'ไม่มีการคืนสำหรับออเดอร์นี้',
          ),
      );
    };

    async function refresh(): Promise<void> {
      el('order-list').innerHTML = 'กำลังโหลด…';
      try {
        const { items, total } = await api.listOrders({
          from: opt(filterForm, 'from') ?? '',
          to: opt(filterForm, 'to') ?? '',
          channelCode: opt(filterForm, 'channelCode') ?? '',
          search: opt(filterForm, 'search') ?? '',
          unpaidOnly: (filterForm.elements.namedItem('unpaidOnly') as HTMLInputElement).checked,
          limit: state.limit,
          offset: state.offset,
        });
        const unpaid = items.filter((o: Row) => !o['paid_time']).length;
        el('order-list').innerHTML =
          `<p class="muted">พบ ${total ?? items.length} ออเดอร์${
            unpaid > 0 ? ` · <span class="warn-text">${unpaid} ออเดอร์ยังไม่ได้รับเงิน (ยังไม่รับรู้ในรายงาน)</span>` : ''
          }</p>` +
          dataTable(
            ['เลขออเดอร์', 'ช่องทาง', 'สถานะ', 'วันที่รับรู้ (เงินเข้า)', 'statement_time', 'ยอดขาย', 'ส่วนลดร้าน', 'คืน', 'ข้อมูล'],
            items.map((o: Row) => {
              const refunds = (o['returns_refunds'] as Row[] | undefined) ?? [];
              const refundTotal = refunds.reduce((s, r) => s + Number(r['refund_amount'] ?? 0), 0);
              return {
                cls: o['paid_time'] ? undefined : 'row-warn',
                attrs: `data-order="${esc(o['id'])}" tabindex="0" role="button"`,
                cells: [
                  esc(o['external_order_id']),
                  esc(CHANNEL_LABELS[String(o['channel_code'])] ?? o['channel_code']),
                  esc(o['status'] ?? '—'),
                  o['paid_time'] ? ts(o['paid_time']) : '<span class="warn-text">ยังไม่โอน</span>',
                  ts(o['statement_time']),
                  money(o['gross_sales']),
                  money(o['seller_discount']),
                  refundTotal > 0 ? `<span class="neg-num">${money(-refundTotal)}</span>` : '—',
                  o['completeness'] === 'complete' ? pill('ครบ', 'ok') : pill('ไม่สมบูรณ์', 'warn'),
                ],
              };
            }),
            'ไม่พบออเดอร์ตามตัวกรอง',
          ) +
          pagerHtml(total ?? items.length, state.limit, state.offset);
        bindPager(el('order-list'), state, refresh);
        el('order-list')
          .querySelectorAll<HTMLElement>('tr[data-order]')
          .forEach((tr) => {
            const open = (): void => {
              void openOrder(tr.dataset['order']!).catch((e: unknown) =>
                showDrawer(root, 'รายละเอียดออเดอร์', errorBox(e)),
              );
            };
            tr.addEventListener('click', open);
            tr.addEventListener('keydown', (e) => {
              if ((e as KeyboardEvent).key === 'Enter') open();
            });
          });
      } catch (e) {
        el('order-list').innerHTML = errorBox(e);
      }
    }

    const loadSettlements = async (): Promise<void> => {
      try {
        const { items } = await api.listSettlements();
        el('settlement-list').innerHTML = dataTable(
          ['เลขที่ statement', 'statement_time', 'วันที่เงินเข้า (รับรู้)', 'ยอดรวม', 'ค่าธรรมเนียม', 'โอนสุทธิ'],
          items.map((s: Row) => ({
            cells: [
              esc(s['external_statement_id']),
              ts(s['statement_time']),
              s['paid_time'] ? `<strong>${ts(s['paid_time'])}</strong>` : '<span class="warn-text">ยังไม่โอน</span>',
              money(s['gross_amount']),
              money(s['total_fees']),
              money(s['net_payout']),
            ],
          })),
          'ยังไม่มีรอบเงินโอน (เชื่อม TikTok Shop หรือนำเข้าไฟล์ก่อน)',
        );
      } catch (e) {
        el('settlement-list').innerHTML = errorBox(e);
      }
    };

    const loadRefunds = async (): Promise<void> => {
      try {
        const { items } = await api.listRefunds();
        el('refund-list').innerHTML = dataTable(
          ['วันที่คืน', 'ออเดอร์เดิม', 'ช่องทาง', 'จำนวน', 'ยอดคืน', 'เหตุผล', 'สถานะ'],
          items.map((r: Row) => {
            const o = (r['orders'] as Row | null) ?? null;
            return {
              cells: [
                ts(r['refund_time']),
                esc(o?.['external_order_id'] ?? '—'),
                esc(CHANNEL_LABELS[String(o?.['channel_code'])] ?? o?.['channel_code'] ?? '—'),
                esc(r['quantity']),
                `<span class="neg-num">${money(-Number(r['refund_amount'] ?? 0))}</span>`,
                esc(r['reason'] ?? '—'),
                esc(r['status'] ?? '—'),
              ],
            };
          }),
          'ยังไม่มีรายการคืน',
        );
      } catch (e) {
        el('refund-list').innerHTML = errorBox(e);
      }
    };

    filterForm.addEventListener('submit', (e) => {
      e.preventDefault();
      state.offset = 0;
      void refresh();
    });
    void refresh();
    void loadSettlements();
    void loadRefunds();
  },
};
